import { DateTime } from '@ksyos/date-time';
import { Button, ButtonRow, Form, FormGroup } from '@ksyos/design-system';
import { useState } from 'react';
import { useTheme } from 'styled-components';
import type { IPatientInvoiceServicePrice } from '@common/interfaces/patientInvoiceServicePrice';
import type { IPatientInvoiceServicePriceAddEditValidated } from '@common/validation/patientInvoiceServicePrice';
import type { ValidationErrors } from '@frontend/utils';
import { patientInvoiceServicePriceAddEditSchema } from '@common/validation/patientInvoiceServicePrice';
import { InputCurrency } from '@frontend/components/elements/common';
import { FormContextProvider } from '@frontend/components/elements/formContext';
import { IndependentDateInput } from '@frontend/components/fields/Date';
import { translateKey } from '@frontend/translate';
import { emptyValidationErrors, updateValidationErrorsForOne, validate } from '@frontend/utils';

interface IPatientInvoiceServicePriceAddEditProps {
    onCancel: () => void;
    onChange: (key: keyof IPatientInvoiceServicePrice, value: string | number | Date | null) => void;
    onSave: (patientInvoiceServicePrice: IPatientInvoiceServicePriceAddEditValidated) => void;
    patientInvoiceServicePrice: Partial<IPatientInvoiceServicePrice>;
}

export function PatientInvoiceServicePriceAddEdit(props: IPatientInvoiceServicePriceAddEditProps) {
    const [validationErrors, setValidationErrors] = useState<ValidationErrors>(emptyValidationErrors);
    const { startDate, endDate, price } = props.patientInvoiceServicePrice;
    const theme = useTheme();

    return (
        <>
            <Form
                submitOnEnter={false}
                sx={{ padding: '46px 50px 50px', backgroundColor: theme.colors.white }}
            >
                <FormContextProvider
                    value={{
                        errors: validationErrors,
                        item: { price: props.patientInvoiceServicePrice },
                        onChange: (key, value) => {
                            handleChange(key, value);
                            validateOne(key, value);
                        },
                        onDoValidate: () => {
                            return;
                        },
                        placeholderGroupName: 'price',
                    }}
                >
                    <FormGroup label={translateKey('common.startDate')}>
                        <IndependentDateInput
                            error={validationErrors.get('startDate')}
                            hasMaxWidth={true}
                            id="startDate"
                            onChange={(value) => handleChange('startDate', value)}
                            onDoValidate={(date) => validateOne('startDate', date)}
                            onEnterPress={(value) => handleChange('startDate', value)}
                            placeholder={translateKey('common.datePlaceholder')}
                            size="large"
                            value={getDisplayDate(startDate)}
                            variant="outline"
                        />
                    </FormGroup>
                    <FormGroup label={translateKey('common.endDate')}>
                        <IndependentDateInput
                            error={validationErrors.get('endDate')}
                            hasMaxWidth={true}
                            id="endDate"
                            onChange={(value) => handleChange('endDate', value)}
                            onDoValidate={(date) => validateOne('endDate', date)}
                            onEnterPress={(value) => handleChange('endDate', value)}
                            placeholder={translateKey('common.datePlaceholder')}
                            size="large"
                            value={getDisplayDate(endDate)}
                            variant="outline"
                        />
                    </FormGroup>
                    <FormGroup label={translateKey('common.price')}>
                        <InputCurrency
                            error={validationErrors.get('price')}
                            fieldKey="price"
                            onChange={(value) => handleChange('price', value)}
                            placeholder=""
                            value={price ? price : null}
                        />
                    </FormGroup>
                    <ButtonRow justify="right">
                        <>
                            <Button
                                onClick={props.onCancel}
                                variant="outline"
                            >
                                {translateKey('common.cancel')}
                            </Button>
                            <Button
                                onClick={() => handleSave()}
                                variant="solid"
                            >
                                {translateKey('common.save')}
                            </Button>
                        </>
                    </ButtonRow>
                </FormContextProvider>
            </Form>
        </>
    );

    function handleChange(key: keyof IPatientInvoiceServicePrice, value: string | number | Date | null) {
        props.onChange(key, value);
    }

    function getDisplayDate(date: string | Date | null | undefined) {
        return date ? DateTime.from(date).format('YYYY-MM-DD') : null;
    }

    function handleSave() {
        const { errors, item } = validate(patientInvoiceServicePriceAddEditSchema, props.patientInvoiceServicePrice);

        if (errors.size > 0) {
            setValidationErrors(errors);

            return;
        }
        if (item) {
            props.onSave(item);
        }
    }

    async function validateOne(name: keyof IPatientInvoiceServicePrice, value?: string | Date | null | undefined) {
        setValidationErrors(
            updateValidationErrorsForOne(
                patientInvoiceServicePriceAddEditSchema,
                { ...props.patientInvoiceServicePrice, ...(value && { [name]: value }) },
                validationErrors,
                name
            )
        );
    }
}
